/**
 * Web Push: SW registration, subscription bookkeeping, install heuristics.
 *
 * Surface (used by main.js, /me, /home, the post-register prompt):
 *
 *   registerServiceWorker() — install + claim the SW. Idempotent.
 *   getVapidConfig()        — cached fetch of /push/vapid-public-key.
 *   isPushSupported()       — Chrome/Android: yes once installed or
 *                             standalone. iOS: only when installed as PWA
 *                             (Safari refuses subscribe() outside
 *                             standalone mode).
 *   permissionState()       — 'granted' | 'denied' | 'default' | 'unsupported'
 *   requestPermissionAndSubscribe()  — the user-gesture path: ask for
 *                             permission, subscribe, POST to /push/subscribe.
 *   ensureSubscriptionSynced() — on every authed app boot: if permission is
 *                             granted, make sure we have a sub AND the
 *                             server knows about it. Covers the
 *                             pushsubscriptionchange handoff from the SW
 *                             (which can't carry the user's JWT).
 *   unsubscribeThisDevice() — drop the current device's sub on server +
 *                             browser.
 *   isStandalonePWA()       — true if the app is launched from the home
 *                             screen (display-mode: standalone or iOS
 *                             navigator.standalone).
 *   isIOS()                 — iOS detection (Safari + WebViews); used by
 *                             the install card to show the "Add to Home
 *                             Screen" instructions modal instead of the
 *                             Chrome `beforeinstallprompt` flow.
 */

import { api, ApiError } from '../api.js';

const SW_URL = '/sw.js';

let _vapidCache = null;
let _vapidPromise = null;

export async function getVapidConfig() {
  if (_vapidCache) return _vapidCache;
  if (_vapidPromise) return _vapidPromise;
  _vapidPromise = (async () => {
    try {
      const body = await api('/push/vapid-public-key');
      _vapidCache = body;
      return body;
    } catch (e) {
      // Network blip or backend disabled — surface as "disabled" so callers
      // gracefully no-op instead of hanging on an error.
      return { enabled: false, public_key: null };
    } finally {
      _vapidPromise = null;
    }
  })();
  return _vapidPromise;
}

export function isStandalonePWA() {
  if (typeof window === 'undefined') return false;
  const mq = window.matchMedia && window.matchMedia('(display-mode: standalone)');
  if (mq && mq.matches) return true;
  // iOS Safari pre-PWA-display-mode uses a non-standard property.
  return Boolean(window.navigator && window.navigator.standalone);
}

export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

// Android UA sniff. Used by the install + enable-push how-to cards to
// render device-specific copy ("Tap ⋮ → Install app" vs iOS's
// "Tap Share → Add to Home Screen"). Anything that isn't iOS or
// Android falls through to the generic copy.
export function isAndroid() {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent || '');
}

export function isPushSupported() {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  if (!('Notification' in window)) return false;
  // Safari/iOS only allows push when the app is installed to the home
  // screen and launched in standalone mode.
  if (isIOS() && !isStandalonePWA()) return false;
  return true;
}

export function permissionState() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(SW_URL, { scope: '/' });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('push: SW register failed', e);
    return null;
  }
}

async function ensureRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  if (reg) return reg;
  return registerServiceWorker();
}

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function subscribeAndSync() {
  const { enabled, public_key: publicKey } = await getVapidConfig();
  if (!enabled || !publicKey) return null;
  const reg = await ensureRegistration();
  if (!reg) return null;
  // pushManager.ready awaits an active SW; getRegistration may return a
  // registration with only `installing` set on a brand-new install.
  await navigator.serviceWorker.ready;

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  const json = sub.toJSON();
  await api('/push/subscribe', {
    method: 'POST',
    body: {
      endpoint: json.endpoint,
      p256dh: json.keys && json.keys.p256dh,
      auth: json.keys && json.keys.auth,
    },
  });
  return sub;
}

export async function requestPermissionAndSubscribe() {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  let perm = Notification.permission;
  if (perm === 'default') {
    perm = await Notification.requestPermission();
  }
  if (perm !== 'granted') {
    return { ok: false, reason: perm };
  }
  try {
    const sub = await subscribeAndSync();
    if (!sub) return { ok: false, reason: 'no_sub' };
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: 'sync_failed', error: e };
  }
}

export async function ensureSubscriptionSynced() {
  // Called on every authed app boot. If the user has permission AND
  // push is supported, make sure the local browser sub is registered
  // with the backend. Covers the SW's pushsubscriptionchange handoff
  // where the new sub couldn't be POSTed from inside the worker.
  if (!isPushSupported()) return;
  if (permissionState() !== 'granted') return;
  try {
    await subscribeAndSync();
  } catch (e) {
    // Best-effort — never throw during boot.
    if (!(e instanceof ApiError) || e.status >= 500) {
      // eslint-disable-next-line no-console
      console.warn('push: subscription sync failed', e);
    }
  }
}

const ASK_KEY = 'triviana.push_last_asked_at';
const ASK_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * High-intent moment hook (called after a user joins/registers for a
 * trivia, accepts a duel invite, etc.). No-op if push is unsupported,
 * already granted, already denied, or we asked within the last week.
 * The browser's native permission prompt is the actual UI — we just gate
 * the moment of asking.
 */
export async function maybePromptForReminders() {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  const perm = permissionState();
  if (perm === 'granted') {
    // Already opted in; quietly ensure the sub is in sync.
    void ensureSubscriptionSynced();
    return { ok: true, reason: 'already_granted' };
  }
  if (perm === 'denied') return { ok: false, reason: 'denied' };
  // Cooldown so we don't spam the user with the prompt every time.
  const lastAsked = Number(localStorage.getItem(ASK_KEY) || 0);
  if (lastAsked && Date.now() - lastAsked < ASK_COOLDOWN_MS) {
    return { ok: false, reason: 'cooldown' };
  }
  localStorage.setItem(ASK_KEY, String(Date.now()));
  return requestPermissionAndSubscribe();
}

export async function unsubscribeThisDevice() {
  const reg = await ensureRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  // Server-side first; if that fails we still drop the browser sub.
  try {
    await api(`/push/subscribe?endpoint=${encodeURIComponent(endpoint)}`, {
      method: 'DELETE',
    });
  } catch (_) {
    /* ignore */
  }
  try {
    await sub.unsubscribe();
  } catch (_) {
    /* ignore */
  }
}

// ----- PWA install prompt capture -------------------------------------------

let _deferredInstallPrompt = null;
const _installListeners = new Set();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Chrome/Android: capture the event so we can fire it on a user
    // gesture later (calling prompt() outside a click is rejected).
    e.preventDefault();
    _deferredInstallPrompt = e;
    _installListeners.forEach((fn) => fn(true));
  });
  window.addEventListener('appinstalled', () => {
    _deferredInstallPrompt = null;
    _installListeners.forEach((fn) => fn(false));
  });
}

export function canInstallPrompt() {
  return Boolean(_deferredInstallPrompt);
}

export async function triggerInstallPrompt() {
  if (!_deferredInstallPrompt) return { ok: false, reason: 'no_prompt' };
  _deferredInstallPrompt.prompt();
  const outcome = await _deferredInstallPrompt.userChoice;
  _deferredInstallPrompt = null;
  _installListeners.forEach((fn) => fn(false));
  return { ok: outcome.outcome === 'accepted', outcome: outcome.outcome };
}

export function onInstallPromptAvailableChange(fn) {
  _installListeners.add(fn);
  return () => _installListeners.delete(fn);
}
